/* eslint-disable no-undef */
import React from 'react'
import PropTypes from 'prop-types'
import FieldGroup from '../fieldGroup'
import Grid from '@mui/material/Grid2'
import { Box, Paper, Typography, Divider, InputAdornment, TextField, MenuItem, FormControlLabel, Checkbox } from '@mui/material'
import CheckIcon from '@mui/icons-material/CheckCircleOutline'
import Alert from '@mui/material/Alert'
import UploadImageWidget from '../Shared/UploadImageWidget'
import IconTextField from '../Shared/IconTextField'
import TextEditor from '../Helpers/TextEditor'
import FooterSettings from './footerSettings'
import ThemeSettings from './themeSettings'
import useTranslation from '../../hooks/useTranslation'

const styles = {
  helperText: {
    fontWeight: '400',
    fontSize: '0.75rem',
    color: 'rgba(0, 0, 0, 0.6)',
    marginLeft: '14px',
    display: 'block'
  }
}

const EditStore = props => {
  const { t } = useTranslation()

  return (
    <Grid container spacing={2}>
      {props.store.updating && (
        <Grid size={12}>
          <Alert severity="info">Updating settings...</Alert>
        </Grid>
      )}

      <Grid size={12}>
        <Paper variant="outlined">
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('labels.store_settings.general_settings')}</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <Grid container>
              <Grid container size={{ xs: 12, md: 8 }} spacing={1} sx={{paddingRight: { xs: 0, md: '2%' }}}>
                <Grid size={12}>
                  <FieldGroup
                    id="title_tag"
                    label={t('labels.store_settings.title_tag')}
                    name="title_tag"
                    value={props.store.title_tag}
                    placeholder={t('labels.store_settings.title_tag')}
                    onChange={props.onChange} />
                </Grid>
                <Grid size={12}>
                  <FieldGroup
                    id="seo_description"
                    label={t('labels.store_settings.seo_description')}
                    name="seo_description"
                    value={props.store.seo_description}
                    multiline
                    onChange={props.onChange}
                    helperText={t('labels.store_settings.seo_description')} />
                </Grid>
                <Grid size={12}>
                  <b>{t('labels.store_settings.footer_text')}</b>
                  <TextEditor
                    value={props.store.footer_text}
                    onChange={props.onFooterChange} />
                  <Typography variant="body2" component="p" sx={{ fontWeight: '400', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', marginLeft: '14px', display: 'block' }}>{t('labels.store_settings.footer_text')}</Typography>
                </Grid>
                <Grid size={12}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={props.store.enable_seo}
                            onChange={props.onChange}
                            name="enable_seo"
                          />
                        }
                        label={t('labels.store_settings.enable_seo')}
                      />
                      <Typography variant="body2" sx={{ fontWeight: '400', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', marginLeft: '14px', display: 'block' }}>
                        {t('labels.store_settings.enable_seo')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!props.store.show_stock}
                            onChange={props.onChange}
                            name="show_stock"
                          />
                        }
                        label={t('labels.show_stock')}
                      />
                      <Typography variant="body2" sx={{ fontWeight: '400', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', marginLeft: '14px', display: 'block' }}>
                        {t('labels.show_stock_help')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container size={{ xs: 12, md: 4 }} spacing={2}>
                <Grid container size={12} spacing={2}>
                  <Grid size={12}>
                    <UploadImageWidget
                      object={props.store}
                      onChange={props.onChange}
                      deleteImage={props.removeLogo}
                      title={t('labels.store_settings.logo')}
                      field="logo"
                      size="medium" />
                  </Grid>
                </Grid>
                <Grid container size={12} spacing={2}>
                  <Grid size={12}>
                    <UploadImageWidget
                      object={props.store}
                      onChange={props.onChange}
                      deleteImage={props.removeFavicon}
                      title={t('labels.store_settings.favicon')}
                      field="favicon"
                      description={t('labels.store_settings.icon_helper')}
                      size="medium" />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Paper variant="outlined">
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('labels.store_settings.language_options')}</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <TextField
                  select
                  name="language"
                  value={props.store.language}
                  label={t('labels.language')}
                  placeholder="select"
                  helperText={t('labels.language_helper')}
                  onChange={props.onChange}
                  fullWidth>
                  <MenuItem value="en">{t('labels.english')}</MenuItem>
                  <MenuItem value="sv">{t('labels.swedish')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Paper variant="outlined">
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('labels.store_settings.domains')}</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  id="store_url"
                  label={t('labels.store_settings.store_url')}
                  name="store_url"
                  value={props.store.store_url}
                  placeholder="URL"
                  onBlur={props.validateStoreUrl}
                  onChange={props.onChange}
                  helperText={props.store.store_url ? (
                    <a href={`http://${process.env.REACT_APP_STORE_HOST}/${props.store.store_url}`} target="_blank" rel="noopener noreferrer">{`${process.env.REACT_APP_STORE_HOST}/${props.store.store_url}`}</a>
                  ) : (null)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        { props.urlValid && (<CheckIcon />)}
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  id="primary_domain"
                  label={t('labels.store_settings.custom_domain')}
                  name="primary_domain"
                  value={props.store.primary_domain}
                  placeholder="yourdomain.com"
                  onChange={props.onChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        { props.urlValid && (<CheckIcon />)}
                      </InputAdornment>
                    ),
                  }} />
                {props.store.primary_domain ? (
                  <span>
                      Visit Store:{' '}
                    <a href={`http://${props.store.primary_domain}`} target="_blank" rel="noopener noreferrer">{`${props.store.primary_domain}`}</a>
                  </span>
                ) : (null)
                }
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Paper variant="outlined">
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('labels.store_visibility')}</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <FormControlLabel
              control={<Checkbox checked={props.store.is_live} onChange={props.onChange} name="is_live" />}
              label={t('labels.store_settings.shop_is_live')} />
            <Typography variant="body2" sx={{ fontWeight: '400', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', marginLeft: '14px', display: 'block' }}>{t('labels.store_settings.shop_is_live')}</Typography>
          </Box>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Paper variant="outlined">
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('labels.store_settings.contact_details')}</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('labels.store_settings.company_name')}
                  name="company_name"
                  value={props.store.company_name || ''}
                  onChange={props.onChange}
                  fullWidth/>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('labels.store_settings.store_name')}
                  name="name"
                  value={props.store.name || ''}
                  onChange={props.onChange}
                  fullWidth/>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('labels.store_settings.address')}
                  name="address"
                  value={props.store.address || ''}
                  onChange={props.onChange}
                  fullWidth/>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('labels.store_settings.organization_number')}
                  name="organisation_number"
                  value={props.store.organisation_number || ''}
                  onChange={props.onChange}
                  fullWidth/>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('labels.store_settings.phone')}
                  name="phone"
                  value={props.store.phone || ''}
                  onChange={props.onChange}
                  fullWidth/>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('labels.store_settings.contact_email')}
                  name="email"
                  value={props.store.email || ''}
                  onChange={props.onChange}
                  fullWidth/>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Paper variant="outlined">
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('labels.store_settings.shipping')}</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={!props.store.same_invoice_address} onChange={props.onChange} name="same_invoice_address" />}
                  label={t('labels.store_settings.billing_address_checkout')} />
                <Typography variant="body2" sx={{ fontWeight: '400', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', marginLeft: '14px', display: 'block' }}>{t('labels.store_settings.billing_address_helper')}</Typography>
              </Grid>
              <Grid size={12}>
                <TextField
                  label={t('labels.store_settings.order_copy_email')}
                  name="order_email"
                  value={props.store.order_email || ''}
                  helperText={t('labels.store_settings.order_helper') + `email1@${props.store.store_url}.com, email2@${props.store.store_url}.com`}
                  placeholder={t('labels.store_settings.order_copy_email')}
                  onChange={props.onChange}
                  fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <IconTextField
                  label={t('labels.store_settings.freight_per_order')}
                  name="freight_per_order"
                  value={props.store.freight_per_order || 0}
                  onChange={props.onChange}
                  fullWidth={true}
                  icon="kr"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <IconTextField
                  label={t('labels.store_settings.freight_outside_sweden')}
                  name="freight_outside_sweden"
                  value={props.store.freight_outside_sweden || 0}
                  onChange={props.onChange}
                  fullWidth={true}
                  icon="kr"
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
      <ThemeSettings onChange={props.onChange} store={props.store} styles={styles} />
      <FooterSettings onChange={props.onChange} store={props.store} />
    </Grid>
  )
}

EditStore.propTypes = {
  store: PropTypes.object.isRequired,
  currencies: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  validateStoreUrl: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  showSecondaryColorPicker: PropTypes.bool,
  urlValid: PropTypes.bool,
  onColorFocus: PropTypes.func,
  onColorBlur: PropTypes.func,
  onPrimaryColorChange: PropTypes.func,
  onSecondaryColorChange: PropTypes.func,
  showPrimaryColorPicker: PropTypes.bool,
  onFooterChange: PropTypes.func,
  removeLogo: PropTypes.func.isRequired,
  removeFavicon: PropTypes.func.isRequired
}

export default EditStore
